<template>
    <div class="modal-card card">
        <div class="card-header">
            <h4 class="card-header-title d-flex align-items-center gap-2">
                {{ modalData.title || 'Input' }}
                <span v-if="files.length" class="file-meta-pill">
                    <strong>{{ files.length }} FILE{{ files.length > 1 ? 'S' : '' }}</strong>
                </span>
            </h4>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>

        <!-- Loading state -->
        <div v-if="loading" class="card-body d-flex align-items-center justify-content-center"
            style="min-height: 200px;">
            <div class="text-center text-muted">
                <div class="spinner-border spinner-border-sm mb-2" role="status"></div>
                <div class="small">Loading files...</div>
            </div>
        </div>

        <!-- Error state -->
        <div v-else-if="fetchError" class="card-body">
            <div class="alert alert-danger" role="alert">
                <i class="fa-solid fa-circle-exclamation me-1"></i>
                {{ fetchError }}
            </div>
        </div>

        <!-- Loaded state -->
        <template v-else-if="files.length">
            <div class="card-body">
                <!-- Tab bar -->
                <div class="file-tab-bar">
                    <div class="file-tabs-scroll">
                        <div v-for="(file, index) in files" :key="index" class="file-tab"
                            :class="{ active: activeIndex === index }" @click="activeIndex = index">
                            <i :class="fileIcon(file)" class="me-1"></i>
                            <span class="tab-label">{{ file.name }}</span>
                        </div>
                    </div>
                </div>

                <!-- Active file content -->
                <div v-if="activeFile">
                    <div v-if="isPreviewable(activeFile)">
                        <div
                            v-if="fileExtension(activeFile.name) === 'avro' || fileExtension(activeFile.name) === 'parquet'">
                            <OutputParser v-if="avroFileDetails(activeFile)" :compData="avroFileDetails(activeFile)" />
                            <ul v-else class="list-group list-group-sm bg-warning">
                                <li class="list-group-item">
                                    <i class="fa-solid fa-circle-exclamation"></i> No preview available
                                </li>
                            </ul>
                        </div>
                        <div v-else>
                            <CodemirrorEditor :editorId="`file-${activeIndex}-${fileExtension(activeFile.name)}`"
                                v-model="activeFile.content" :mode="fileExtension(activeFile.name)" :refreshOn="true"
                                :disabled="true" :readOnly="true" :performLint="false" />
                        </div>
                    </div>
                    <div v-else>
                        <ul class="list-group list-group-sm">
                            <li class="list-group-item">
                                <i class="fa-solid fa-circle-exclamation me-1"></i>
                                Preview not available for this file type.
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </template>

        <!-- Empty state -->
        <div v-else class="card-body">
            <div class="text-center py-3 bg-light rounded border border-dashed">
                <span class="text-muted">
                    <i class="fa fa-circle-exclamation me-1"></i>
                    No files found.
                </span>
            </div>
        </div>

        <!-- Footer -->
        <div class="card-footer text-end d-flex align-items-center justify-content-between">
            <div>
                <span class="file-meta-pill" v-if="activeFile?.size">
                    {{ formatBytes(activeFile.size) }}
                </span>
            </div>
            <div class="d-flex gap-2">
                <button v-if="files.length > 1" class="btn btn-outline-primary btn-sm" :disabled="downloadingAll"
                    @click="handleDownloadAll">
                    <div v-if="downloadingAll" class="spinner-border spinner-border-sm" role="status"></div>
                    <span v-else><i class="fa-solid fa-download me-1"></i>Download All</span>
                </button>
                <button class="btn btn-primary btn-sm" :disabled="downloading || !activeFile"
                    @click="handleDownload(activeFile)">
                    <div v-if="downloading" class="spinner-border spinner-border-sm" role="status"></div>
                    <span v-else><i class="fa-solid fa-download me-1"></i>Download</span>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import CodemirrorEditor from "../../components/CodemirrorEditor.vue";
import OutputParser from "../OutputParser.vue";
import { formatBytes } from '../../utils/commonFunction';
import axios from 'axios';
export default {
    components: { CodemirrorEditor, OutputParser },
    name: "AgentInputFileViewer",
    props: {
        modalData: {
            type: [Object, Array, String],
        },
    },
    data() {
        return {
            files: [],
            activeIndex: 0,
            loading: false,
            fetchError: null,
            downloading: false,
            downloadingAll: false,
        };
    },
    computed: {
        activeFile() {
            return this.files[this.activeIndex] || null;
        },
    },
    async beforeMount() {
        let attachedFile = this.modalData.file;
        if (attachedFile && attachedFile.name && attachedFile.content) {
            this.files = [attachedFile];
        } else {
            await this.fetchFiles(attachedFile);
        }
    },
    methods: {
        formatBytes,
        async fetchFiles(attachedFile = null) {
            this.loading = true;
            this.fetchError = null;
            let qp = attachedFile?.name ? `?file_name=${attachedFile.name}` : '';
            try {
                // TODO: Replace with your actual endpoint
                const response = await axios.get(
                    `${this.$store.state.agentsUrl}/v1/aquabot/cids/${this.$store.state.currentClient.cid}/chats/${this.modalData.resourceId}/messages/${this.modalData.messageId}/files${qp}`,
                    {
                        headers: {
                            'Authorization': `Bearer ${this.$store.state.accessToken}`,
                        },
                    }
                );
                this.files = response.data.files || [];
            } catch (err) {
                console.error("Failed to fetch files:", err);
                this.fetchError = "Failed to load file details. Please try again.";
            } finally {
                this.loading = false;
            }
        },

        fileExtension(name = "") {
            return name.split(".").pop().toLowerCase();
        },

        fileIcon(file) {
            const ext = this.fileExtension(file.name);
            const fileTypeIcon = {
                'csv': 'fa-solid fa-file-csv',
                'sql': 'fa-solid fa-database',
                'py': 'fa-brands fa-python',
                'java': 'fa-brands fa-java',
                'js': 'fa-brands fa-square-js',
                'vue': 'fa-brands fa-vuejs',
                'ts': 'fa-brands fa-typescript',
                'go': 'fa-brands fa-golang',
            }
            return fileTypeIcon[ext] || "fa fa-file-alt";
        },

        isPreviewable(file) {
            return !!file.content;
        },

        avroFileDetails(file) {
            if (!file.content) return null;
            return {
                filecontent: file.content,
                filetype: this.fileExtension(file.name),
                filename: file.name,
            };
        },

        handleDownload(file) {
            if (!file || !file.content) return;
            this.downloading = true;
            try {
                this._triggerDownload(file);
            } catch (err) {
                console.error("Download failed:", err);
                this.$store.dispatch("addToast", { message: "Failed to download file.", type: "error" });
            } finally {
                this.downloading = false;
            }
        },

        async handleDownloadAll() {
            this.downloadingAll = true;
            try {
                for (const file of this.files) {
                    if (file.content) this._triggerDownload(file);
                }
            } catch (err) {
                console.error("Download all failed:", err);
                this.$store.dispatch("addToast", { message: "Failed to download files.", type: "error" });
            } finally {
                this.downloadingAll = false;
            }
        },

        _triggerDownload(file) {
            const ext = this.fileExtension(file.name);
            const isBinary = ext === "avro" || ext === "parquet";
            const fileContent = isBinary ? [this.decodeFileContent(file.content)] : [file.content];
            const mimeType = isBinary ? "application/octet-stream" : (file.mimeType || "text/plain;charset=utf-8;");
            const blob = new Blob(fileContent, { type: mimeType });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", file.name);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        },

        decodeFileContent(content) {
            const binaryStr = atob(JSON.parse(JSON.stringify(content)));
            const bytes = new Uint8Array(binaryStr.length);
            for (let i = 0; i < binaryStr.length; i++) {
                bytes[i] = binaryStr.charCodeAt(i);
            }
            return bytes;
        },
    },
};
</script>

<style scoped>
/* ── Tab bar ─────────────────────────────────────────── */
.file-tab-bar {
    border-bottom: 1.5px solid rgba(79, 142, 247, 0.18);
    background: transparent;
}

.file-tabs-scroll {
    display: flex;
    overflow-x: auto;
    scrollbar-width: thin;
    scrollbar-color: rgba(79, 142, 247, 0.3) transparent;
    padding-bottom: 0;
}

.file-tabs-scroll::-webkit-scrollbar {
    height: 3px;
}

.file-tabs-scroll::-webkit-scrollbar-thumb {
    background: rgba(79, 142, 247, 0.35);
    border-radius: 2px;
}

.file-tab {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 0.4rem 0.75rem;
    font-size: 0.8rem;
    font-family: 'JetBrains Mono', 'Fira Code', monospace;
    font-weight: 500;
    color: #6c757d;
    border-radius: 6px 6px 0 0;
    cursor: pointer;
    white-space: nowrap;
    border: 1px solid transparent;
    border-bottom: none;
    transition: color 0.15s ease, background 0.15s ease;
    position: relative;
    bottom: -1px;
    user-select: none;
}

.file-tab.active {
    color: var(--bs-primary);
    background: rgba(var(--bs-primary-rgb), 0.1);
    border-color: rgba(var(--bs-primary-rgb), 0.1);
    font-weight: 700;
}

.tab-label {
    max-width: 140px;
    overflow: hidden;
    text-overflow: ellipsis;
}

/* ── File meta bar ───────────────────────────────────── */
.file-name-container {
    background: rgba(79, 142, 247, 0.1);
    font-family: 'JetBrains Mono', 'Fira Code', monospace;
    font-size: 0.8rem;
    padding: 0.2rem 0.5rem;
    border-radius: 5px;
    font-weight: 600;
    color: #4f8ef7;
    border: 1px solid rgba(79, 142, 247, 0.25);
}

.file-meta-pill {
    font-family: 'JetBrains Mono', 'Fira Code', monospace;
    font-size: 0.72rem;
    padding: 0.15rem 0.45rem;
    border-radius: 4px;
    background: rgba(108, 117, 125, 0.08);
    color: #6c757d;
    border: 1px solid rgba(108, 117, 125, 0.15);
}
</style>
<style lang="scss">
body.dark-mode {
    .file-tab.active {
        color: #4f8ef7;
    }
}
</style>